import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, RotateCcw, ShieldCheck } from "lucide-react";

const FIELDS = [
  { key: "no_leads_min_spend",     label: "No leads · spend ≥",          unit: "₹",    severity: "critical", help: "Fire when a campaign spends ≥ this amount today and produces 0 leads." },
  { key: "budget_pacing_pct",      label: "Monthly budget pacing ≥",    unit: "%",    severity: "critical", help: "Fire when monthly spend exceeds this % AND is ≥15pp ahead of expected day pace." },
  { key: "cpl_spike_pct",          label: "CPL spike (week-over-week)", unit: "%",    severity: "critical", help: "Fire when 7-day CPL is up by this % vs the previous 7 days. (CPL threshold)" },
  { key: "ctr_drop_pct",           label: "CTR drop",                    unit: "%",    severity: "warning",  help: "Fire when CTR drops by this % vs prior period." },
  { key: "cpc_increase_pct",       label: "CPC increase",                unit: "%",    severity: "warning",  help: "Fire when CPC rises by this % vs prior period." },
  { key: "phone_call_drop_pct",    label: "Phone-call drop",             unit: "%",    severity: "warning",  help: "Fire when phone calls drop by this % vs prior 7 days." },
  { key: "lead_drop_pct",          label: "Lead-volume drop",            unit: "%",    severity: "warning",  help: "Fire when lead count drops by this % vs prior 7 days (min 5 prior leads). (Lead drop threshold)" },
  { key: "lost_is_budget_pct",     label: "Search lost IS · budget",     unit: "%",    severity: "warning",  help: "Fire when search-impression-share lost to budget exceeds this %." },
  { key: "lost_is_rank_pct",       label: "Search lost IS · rank",       unit: "%",    severity: "warning",  help: "Fire when search-impression-share lost to rank exceeds this %." },
  { key: "duplicate_increase_pct", label: "Duplicate-leads increase",    unit: "%",    severity: "warning",  help: "Fire when duplicate lead rate rises by this % vs prior 7 days." },
  // v1.4.2.1 user-requested:
  { key: "daily_spend_max",        label: "Daily spend cap (per campaign)", unit: "₹", severity: "critical", help: "Fire when a single campaign spends more than this ₹ amount in one day. Set to 0 to disable. (Spend threshold)" },
  { key: "spend_spike_pct",        label: "Sudden spend spike",          unit: "%",    severity: "critical", help: "Fire when today's spend is this % of the prior 7-day daily average (e.g. 200 = 2× normal)." },
  { key: "campaign_inactivity_days", label: "Campaign inactivity",        unit: "days", severity: "warning",  help: "Fire when an enabled campaign has zero impressions for this many consecutive days." },
  { key: "conversion_drop_pct",    label: "Conversion drop (vs 7-day avg)", unit: "%", severity: "warning",  help: "Fire when today's conversions drop by this % vs the prior 7-day average." },
  { key: "qualified_call_drop_pct", label: "Qualified call lead drop",   unit: "%",    severity: "warning",  help: "Fire when today's qualified-call leads (>15s) drop by this % vs prior 7-day average." },
];

export default function AlertThresholdsPanel() {
  const [defaults, setDefaults] = useState({});
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [activeId, setActiveId] = useState("_default");
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    try {
      const [{ data: list }, { data: cs }] = await Promise.all([
        api.get("/admin/alert-thresholds"),
        api.get("/clients").catch(() => ({ data: [] })),
      ]);
      setDefaults(list.defaults || {});
      setRows(list.rows || []);
      setClients(cs || []);
    } catch (e) {
      toast.error("Could not load alert thresholds");
    }
  };

  const loadOne = async (clientId) => {
    setActiveId(clientId);
    try {
      const { data } = await api.get(`/admin/alert-thresholds/${clientId}`);
      setDraft(data.effective || {});
    } catch (e) {
      toast.error("Could not load threshold details");
    }
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (activeId) loadOne(activeId); /* eslint-disable-next-line */ }, [activeId]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach((f) => {
        const v = draft[f.key];
        if (v !== "" && v != null && !Number.isNaN(Number(v))) payload[f.key] = Number(v);
      });
      await api.put(`/admin/alert-thresholds/${activeId}`, payload);
      toast.success(activeId === "_default" ? "Organisation defaults saved" : "Client thresholds saved");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const removeOverride = async () => {
    if (activeId === "_default") return toast.error("Cannot delete the organisation defaults — only override fields.");
    if (!window.confirm("Remove this client's overrides? It will fall back to org defaults.")) return;
    try {
      await api.delete(`/admin/alert-thresholds/${activeId}`);
      toast.success("Override removed");
      setActiveId("_default");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const resetToDefault = (key) => setDraft({ ...draft, [key]: defaults[key] });

  const overrideClientIds = new Set(rows.filter((r) => r.client_id !== "_default").map((r) => r.client_id));
  const orgDefaultsRow = rows.find((r) => r.client_id === "_default");

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6" data-testid="alert-thresholds-panel">
      {/* Left rail: client picker */}
      <aside className="space-y-1.5">
        <div className="label-mono mb-2">Scope</div>
        <button
          onClick={() => setActiveId("_default")}
          data-testid="threshold-scope-default"
          className={`w-full text-left px-3 py-2.5 rounded-sm text-sm border transition-colors ${
            activeId === "_default" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"
          }`}
        >
          <div className="flex items-center gap-2 font-medium">
            <ShieldCheck className="w-4 h-4" />
            Organisation defaults
            {orgDefaultsRow && <span className="ml-auto text-[10px] opacity-70">customised</span>}
          </div>
          <div className={`text-[11px] mt-0.5 ${activeId === "_default" ? "opacity-80" : "text-muted-foreground"}`}>
            Applies to every client without override
          </div>
        </button>

        <div className="label-mono mt-4 mb-2">Per-client overrides</div>
        <div className="space-y-1">
          {clients.length === 0 && (
            <div className="text-xs text-muted-foreground italic px-2">No clients yet.</div>
          )}
          {clients.map((c) => (
            <button
              key={c.id}
              onClick={() => setActiveId(c.id)}
              data-testid={`threshold-scope-${c.id}`}
              className={`w-full text-left px-3 py-2 rounded-sm text-sm border transition-colors ${
                activeId === c.id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="truncate flex-1">{c.name}</span>
                {overrideClientIds.has(c.id) && (
                  <span className={`text-[10px] uppercase tracking-wider ${activeId === c.id ? "opacity-80" : "text-[hsl(var(--ai))]"}`}>
                    custom
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Right pane: editor */}
      <div className="bg-card border border-border rounded-sm p-5">
        <div className="flex items-start justify-between mb-1">
          <div>
            <div className="label-mono">Editing</div>
            <div className="font-heading text-lg tracking-tight">
              {activeId === "_default"
                ? "Organisation defaults"
                : clients.find((c) => c.id === activeId)?.name || "Client thresholds"}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Resolution order on each alert tick: <strong>per-client override</strong> → <strong>organisation default</strong> → <strong>built-in</strong>. Empty fields fall back automatically.
            </p>
          </div>
          {activeId !== "_default" && overrideClientIds.has(activeId) && (
            <button
              onClick={removeOverride}
              data-testid="threshold-remove-override"
              className="h-9 px-3 text-xs font-medium border border-[hsl(var(--critical))]/30 text-[hsl(var(--critical))] hover:bg-[hsl(var(--critical))]/10 rounded-sm inline-flex items-center gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" /> Remove override
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
          {FIELDS.map((f) => {
            const builtIn = defaults[f.key];
            const value = draft[f.key];
            const overridden = value != null && Number(value) !== Number(builtIn);
            return (
              <div key={f.key} className={`border border-border rounded-sm p-3 ${overridden ? "border-l-2 border-l-[hsl(var(--ai))]" : ""}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm font-medium">{f.label}</div>
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm ${
                    f.severity === "critical" ? "bg-[hsl(var(--critical))]/10 text-[hsl(var(--critical))]"
                                               : "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]"
                  }`}>{f.severity}</span>
                </div>
                <p className="text-[11px] text-muted-foreground mb-2">{f.help}</p>
                <div className="flex items-center gap-2">
                  <span className="label-mono w-4 text-right">{f.unit}</span>
                  <input
                    type="number"
                    value={value ?? ""}
                    onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                    data-testid={`threshold-input-${f.key}`}
                    className="flex-1 h-9 px-2 border border-border rounded-sm text-sm tabular focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder={builtIn != null ? String(builtIn) : ""}
                  />
                  {overridden && (
                    <button
                      onClick={() => resetToDefault(f.key)}
                      title={`Reset to ${builtIn}`}
                      data-testid={`threshold-reset-${f.key}`}
                      className="h-9 w-9 inline-flex items-center justify-center border border-border rounded-sm hover:bg-secondary"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  Default: <span className="tabular">{builtIn ?? "—"}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-2 mt-6 pt-4 border-t border-border">
          <button
            onClick={save}
            disabled={saving}
            data-testid="threshold-save"
            className="h-10 px-5 bg-primary text-primary-foreground text-sm font-semibold rounded-sm inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save thresholds"}
          </button>
          <button
            onClick={() => loadOne(activeId)}
            className="h-10 px-4 border border-border text-sm rounded-sm hover:bg-secondary"
          >
            Discard changes
          </button>
          <div className="flex-1" />
          {activeId === "_default" && (
            <span className="text-xs text-muted-foreground">
              These values flow to every client without an override.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
